import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { PipecatClient } from "@pipecat-ai/client-js";
import { WebSocketTransport } from "@pipecat-ai/websocket-transport";
import {
  PhoneCall, PhoneDisconnect, SignOut, SquaresFour, Sparkle,
  ShieldCheck, ChatCircle, WaveSine, Notebook, Plus, Trash, CaretRight,
} from "@phosphor-icons/react";

const FONT_SERIF = "'Cormorant Garamond', serif";
const FONT_SANS = "'Manrope', system-ui, sans-serif";

// same palette as /app/design_guidelines.json (mirrors VoiceChat.jsx)
const C = {
  appBg: "#F9F8F6",
  centerBg: "#FFFFFF",
  sidebarBg: "#F3F1EC",
  userBubble: "#F3F1EC",
  textPrimary: "#1C241C",
  textSecondary: "#5C665A",
  accent: "#9E8155",
  borderSubtle: "#E5E1D8",
};

export default function RealtimeVoiceChat() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const [bootstrap, setBootstrap] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [turns, setTurns] = useState([]); // { role: 'user'|'brand', text }
  const [status, setStatus] = useState("Tap to start the call");

  const clientRef = useRef(null);
  const scrollRef = useRef(null);

  // ---- bootstrap on mount (this surface's own session list — realtime calls
  // are scoped separately from text chat and tap-to-talk voice)
  useEffect(() => {
    api.get("/relationship/bootstrap", { params: { channel: "voice_live" } })
      .then(({ data }) => setBootstrap(data))
      .catch(() => toast.error("Couldn't load your profile"));
  }, []);

  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [turns]);

  useEffect(() => () => { clientRef.current?.disconnect(); }, []);

  const sessions = bootstrap?.sessions || [];

  const sessionGroups = useMemo(() => {
    const groups = {};
    for (const s of sessions) {
      const d = new Date(s.updated_at || s.created_at);
      const key = isToday(d) ? "Today" : isYesterday(d) ? "Yesterday" : d.toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" });
      (groups[key] ||= []).push(s);
    }
    return Object.entries(groups);
  }, [sessions]);

  const refreshBootstrap = async () => {
    try {
      const { data } = await api.get("/relationship/bootstrap", { params: { channel: "voice_live" } });
      setBootstrap(data);
    } catch {
      // non-fatal — sidebar just won't refresh
    }
  };

  const openSession = async (sid) => {
    if (connected || connecting) {
      toast.error("End the current call before viewing a past conversation");
      return;
    }
    try {
      const { data } = await api.get(`/relationship/sessions/${sid}`);
      const events = data.events || [];
      const loaded = [];
      for (const e of events) {
        loaded.push({ role: "user", text: e.query });
        loaded.push({ role: "brand", text: e.answer });
      }
      setTurns(loaded);
      setSessionId(sid);
    } catch {
      toast.error("Couldn't open that conversation");
    }
  };

  const newSession = () => {
    if (connected || connecting) return;
    setTurns([]);
    setSessionId(null);
  };

  const deleteSession = async (sid, e) => {
    e?.stopPropagation();
    if (!window.confirm("End this conversation? It will be removed from your history.")) return;
    try {
      await api.delete(`/relationship/sessions/${sid}`);
      if (sessionId === sid) newSession();
      await refreshBootstrap();
      toast.success("Conversation removed");
    } catch {
      toast.error("Couldn't remove");
    }
  };

  const onLogout = () => { logout(); nav("/"); };

  const startCall = async () => {
    if (connecting || connected) return;
    setConnecting(true);
    setStatus("Connecting…");
    try {
      // Each call starts a fresh session — past conversations are viewable
      // read-only via the sidebar but not resumable mid-call.
      setTurns([]);
      setSessionId(null);
      const token = localStorage.getItem("av_token");
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      const wsUrl = backend.replace(/^http/, "ws") + `/api/ws/chat/voice-live?token=${encodeURIComponent(token)}`;

      const client = new PipecatClient({
        transport: new WebSocketTransport(),
        enableMic: true,
        enableCam: false,
        callbacks: {
          onBotReady: () => {
            setConnected(true);
            setConnecting(false);
            setStatus("Listening…");
          },
          onUserTranscript: (data) => {
            if (!data.final || !data.text?.trim()) return;
            setTurns((t) => [...t, { role: "user", text: data.text }]);
          },
          onBotTranscript: (data) => {
            if (!data.text?.trim()) return;
            setTurns((t) => [...t, { role: "brand", text: data.text }]);
          },
          onBotStartedSpeaking: () => setStatus("Speaking…"),
          onBotStoppedSpeaking: () => setStatus("Listening…"),
          onDisconnected: () => {
            setConnected(false);
            setConnecting(false);
            setStatus("Tap to start the call");
            refreshBootstrap();
          },
          onError: (e) => {
            toast.error(e?.message || "Voice call hit a snag");
          },
        },
      });
      clientRef.current = client;

      await client.connect({ ws_url: wsUrl });
    } catch (e) {
      toast.error(e?.message || "Couldn't start the call");
      setConnecting(false);
      setStatus("Tap to start the call");
    }
  };

  const endCall = async () => {
    await clientRef.current?.disconnect();
    clientRef.current = null;
    setConnected(false);
    setConnecting(false);
    setStatus("Tap to start the call");
  };

  return (
    <div
      className="h-screen w-screen grid overflow-hidden"
      style={{
        gridTemplateColumns: "290px 1fr",
        background: C.appBg,
        fontFamily: FONT_SANS,
        color: C.textPrimary,
      }}
      data-testid="realtime-voice-layout-wrapper"
    >
      {/* ================= LEFT: identity / nav sidebar ================= */}
      <aside
        className="h-full flex flex-col overflow-hidden"
        style={{ background: C.sidebarBg, borderRight: `1px solid ${C.borderSubtle}` }}
        data-testid="realtime-voice-sidebar-left"
      >
        <div className="px-6 pt-7 pb-4">
          <div className="flex items-center gap-2 mb-1" style={{ color: C.accent }}>
            <Sparkle size={14} weight="fill" />
            <span className="text-[11px] tracking-[0.18em] uppercase font-medium">ITC Concierge</span>
          </div>
          <div style={{ fontFamily: FONT_SERIF, fontSize: 26, lineHeight: 1.1, color: C.textPrimary }} className="tracking-tight">
            Live voice call
          </div>
        </div>

        <div className="px-4 pb-3 space-y-2">
          <Link
            to="/voice-chat"
            data-testid="realtime-back-to-voice-chat"
            className="w-full flex items-center gap-2 px-4 py-3 transition-all"
            style={{ background: "#FFFFFF", border: `1px solid ${C.borderSubtle}`, borderRadius: 10, color: C.textPrimary }}
          >
            <ChatCircle size={14} /> Switch to tap-to-talk
          </Link>
          <button
            onClick={newSession}
            disabled={connected || connecting}
            data-testid="realtime-new-session-btn"
            className="w-full flex items-center justify-between px-4 py-3 transition-all disabled:opacity-40"
            style={{
              background: "#FFFFFF",
              border: `1px solid ${C.borderSubtle}`,
              borderRadius: 10,
              color: C.textPrimary,
            }}
          >
            <span className="flex items-center gap-2 text-[14px]">
              <Plus size={14} /> New conversation
            </span>
            <CaretRight size={12} style={{ color: C.accent }} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-2 pb-4" data-testid="realtime-session-list">
          {sessionGroups.length === 0 ? (
            <div className="px-4 py-8 text-sm italic" style={{ color: C.textSecondary }}>
              Your past calls will live here.
            </div>
          ) : (
            sessionGroups.map(([label, items]) => (
              <div key={label} className="mb-4">
                <div className="px-4 py-2 text-[10px] tracking-[0.18em] uppercase font-medium" style={{ color: C.textSecondary }}>
                  {label}
                </div>
                {items.map((s) => (
                  <div
                    key={s.id}
                    onClick={() => openSession(s.id)}
                    className={`group px-4 py-3 mb-0.5 cursor-pointer transition-all flex items-start gap-2 ${sessionId === s.id ? "" : "hover:bg-white"}`}
                    style={{
                      borderRadius: 8,
                      background: sessionId === s.id ? "#FFFFFF" : "transparent",
                      border: `1px solid ${sessionId === s.id ? C.borderSubtle : "transparent"}`,
                    }}
                    data-testid={`realtime-session-item-${s.id}`}
                  >
                    <Notebook size={14} style={{ color: C.accent, marginTop: 3 }} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[14px] truncate" style={{ color: C.textPrimary }}>
                        {s.title || "Untitled call"}
                      </div>
                      <div className="text-[11px] mt-0.5" style={{ color: C.textSecondary }}>
                        {s.turn_count || 0} {s.turn_count === 1 ? "exchange" : "exchanges"}
                        {s.topic_tags?.[0] ? ` · ${s.topic_tags[0]}` : ""}
                      </div>
                    </div>
                    <button
                      onClick={(e) => deleteSession(s.id, e)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`realtime-session-delete-${s.id}`}
                      title="Remove"
                    >
                      <Trash size={12} style={{ color: C.textSecondary }} />
                    </button>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>

        <div className="px-4 py-4" style={{ borderTop: `1px solid ${C.borderSubtle}` }}>
          <div className="text-[11px] tracking-[0.12em] uppercase mb-1" style={{ color: C.textSecondary }}>
            Signed in as
          </div>
          <div className="text-[14px] truncate" style={{ color: C.textPrimary }}>
            {user?.email}
          </div>
          <div className="flex items-center gap-2 mt-3">
            {user?.role === "admin" && (
              <Link
                to="/admin"
                data-testid="realtime-back-admin-btn"
                className="text-[12px] flex items-center gap-1 px-2 py-1"
                style={{ border: `1px solid ${C.borderSubtle}`, borderRadius: 6, color: C.textPrimary }}
              >
                <SquaresFour size={12} /> Admin
              </Link>
            )}
            <button
              onClick={onLogout}
              data-testid="realtime-logout-btn"
              className="text-[12px] flex items-center gap-1 px-2 py-1"
              style={{ border: `1px solid ${C.borderSubtle}`, borderRadius: 6, color: C.textPrimary }}
            >
              <SignOut size={12} /> Sign out
            </button>
          </div>
        </div>
      </aside>

      {/* ================= CENTER: live call ================= */}
      <section
        className="h-full flex flex-col overflow-hidden"
        style={{ background: C.centerBg }}
        data-testid="realtime-voice-center-area"
      >
        <header
          className="px-10 py-6 flex items-start justify-between gap-6"
          style={{ borderBottom: `1px solid ${C.borderSubtle}` }}
          data-testid="realtime-voice-header"
        >
          <div>
            <div className="flex items-center gap-2 mb-1" style={{ color: C.accent }}>
              <ShieldCheck size={12} weight="fill" />
              <span className="text-[11px] tracking-[0.18em] uppercase font-medium">Relationship Desk · Live call</span>
            </div>
            <div style={{ fontFamily: FONT_SERIF, fontSize: 28, lineHeight: 1.1, color: C.textPrimary }} className="tracking-tight">
              Talk naturally — I'll jump in when you pause.
            </div>
          </div>
        </header>

        <div ref={scrollRef} className="flex-1 overflow-y-auto" data-testid="realtime-voice-message-list">
          <div className="max-w-[720px] mx-auto px-10 py-12">
            {turns.length === 0 && (
              <div data-testid="realtime-voice-empty" className="text-[15px]" style={{ color: C.textSecondary }}>
                Start the call and just speak — no need to tap start/stop per turn. I'll detect when you're done talking and reply out loud.
              </div>
            )}
            <div className="space-y-8">
              {turns.map((t, i) => (
                <div key={i}>
                  {t.role === "user" ? (
                    <div className="flex justify-end" data-testid={`realtime-user-msg-${i}`}>
                      <div
                        className="px-5 py-3 max-w-[85%] text-[15px] leading-relaxed"
                        style={{ background: C.userBubble, color: C.textPrimary, borderRadius: 16 }}
                      >
                        {t.text}
                      </div>
                    </div>
                  ) : (
                    <div data-testid={`realtime-brand-msg-${i}`}>
                      <div
                        style={{ fontFamily: FONT_SERIF, fontSize: 20, lineHeight: 1.55, color: C.textPrimary }}
                        className="whitespace-pre-wrap"
                      >
                        {t.text}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* call control */}
        <div className="px-10 pb-10 pt-4 flex flex-col items-center gap-3" style={{ background: C.centerBg }}>
          <button
            onClick={connected ? endCall : startCall}
            disabled={connecting}
            data-testid={connected ? "realtime-end-call-btn" : "realtime-start-call-btn"}
            className="w-20 h-20 flex items-center justify-center transition-all disabled:opacity-40"
            style={{
              borderRadius: "50%",
              background: connected ? "#B3413B" : C.textPrimary,
              color: "#FFFFFF",
              boxShadow: connected ? `0 0 0 8px ${C.userBubble}` : "none",
            }}
            title={connected ? "End call" : "Start call"}
          >
            {connected ? <PhoneDisconnect size={28} weight="fill" /> : <PhoneCall size={28} weight="fill" />}
          </button>
          <div className="flex items-center gap-1.5 text-[13px]" style={{ color: C.textSecondary }} data-testid="realtime-voice-status">
            <WaveSine size={14} />
            {status}
          </div>
        </div>
      </section>
    </div>
  );
}

// helpers
function isToday(d) {
  const n = new Date();
  return d.toDateString() === n.toDateString();
}
function isYesterday(d) {
  const n = new Date();
  n.setDate(n.getDate() - 1);
  return d.toDateString() === n.toDateString();
}
